import type { ReactNode } from "react";
import { cn } from "./lib/cn";

export type AppShellProps = {
  header?: ReactNode;
  sidebar?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  mainClassName?: string;
};

type AppShellContentProps = Pick<
  AppShellProps,
  "header" | "sidebar" | "children" | "mainClassName"
> & {
  fillContent: boolean;
};

function AppShellContent({
  header,
  sidebar,
  children,
  mainClassName,
  fillContent,
}: AppShellContentProps) {
  const hasSidebar = sidebar != null && sidebar !== false;

  if (hasSidebar) {
    return (
      <div className="flex h-full min-h-0 gap-4">
        <aside className="hidden w-52 shrink-0 md:block lg:w-56">
          {sidebar}
        </aside>
        <div
          className={cn(
            "flex min-h-0 min-w-0 flex-1 flex-col",
            mainClassName,
          )}
        >
          {header}
          {children}
        </div>
      </div>
    );
  }

  const content = fillContent ? (
    <div
      className={cn(
        "flex min-h-0 min-w-0 flex-1 flex-col",
        mainClassName,
      )}
    >
      {children}
    </div>
  ) : (
    children
  );

  return (
    <>
      {header}
      {content}
    </>
  );
}

export function AppShell({
  header,
  sidebar,
  footer,
  children,
  className,
  mainClassName,
}: AppShellProps) {
  const hasFooter = footer != null && footer !== false;

  if (!hasFooter) {
    return (
      <div className={cn("h-full p-4", className)}>
        <AppShellContent
          header={header}
          sidebar={sidebar}
          mainClassName={mainClassName}
          fillContent={false}
        >
          {children}
        </AppShellContent>
      </div>
    );
  }

  return (
    <div className={cn("flex min-h-dvh flex-col", className)}>
      <div className="flex min-h-0 flex-1 flex-col p-4">
        <AppShellContent
          header={header}
          sidebar={sidebar}
          mainClassName={mainClassName}
          fillContent
        >
          {children}
        </AppShellContent>
      </div>
      {footer}
    </div>
  );
}
